/**
 * Rasterises the treemap card (title + subtitle + filters + legend + chart +
 * footer) to a PNG. The rasteriser (html2canvas-pro) is code-split via dynamic
 * import so it only loads on first use. We use the "-pro" fork rather than
 * html2canvas because the page's Tailwind v4 styles resolve to modern
 * `oklch()`/`lab()` colours, which the original html2canvas cannot parse.
 *
 * We capture an off-screen clone with a few export-only tweaks: the page-size
 * title shown, the download link removed, and native <select>s swapped for
 * static boxes (the rasteriser renders live <select>s poorly and a detached
 * clone loses their selected value). Cell fills/labels are inline styles or
 * attributes so they survive the clone.
 */

const FONT_STACK =
  "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";

/** Replace a cloned <select> with a static box showing its live selected value. */
function freezeSelect(
  clone: HTMLSelectElement,
  live: HTMLSelectElement | null
) {
  const opt = live?.options[live.selectedIndex];
  const w = live ? live.offsetWidth : 120;
  const box = document.createElement("div");
  box.style.cssText =
    "box-sizing:border-box;display:flex;align-items:center;gap:6px;width:" +
    w +
    "px;height:31px;padding:6px 9px;font-size:12.5px;line-height:18px;color:#1a1d24;background:#fff;border:1px solid #dfe2e8;border-radius:7px;";
  const text = document.createElement("span");
  text.textContent = opt ? opt.text : clone.value || "";
  text.style.cssText =
    "flex:1 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;";
  const chev = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  chev.setAttribute("width", "10");
  chev.setAttribute("height", "6");
  chev.setAttribute("viewBox", "0 0 10 6");
  chev.style.cssText = "flex:0 0 auto;";
  const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
  p.setAttribute("d", "M1 1l4 4 4-4");
  p.setAttribute("stroke", "#5b616e");
  p.setAttribute("fill", "none");
  p.setAttribute("stroke-width", "1.5");
  chev.appendChild(p);
  box.append(text, chev);
  clone.replaceWith(box);
}

export async function downloadTreemapImage(card: HTMLElement): Promise<void> {
  const { default: h2c } = await import("html2canvas-pro");
  const clone = card.cloneNode(true) as HTMLElement;

  // Show the title at page size (heading-48).
  const title = clone.querySelector<HTMLElement>("[data-tm-title]");
  if (title) {
    title.style.cssText =
      "display:block;font-size:48px;line-height:52px;letter-spacing:-1.44px;font-weight:600;margin:0 0 6px;color:#1a1d24;";
  }

  // Drop the "Download image" link (and its leading separator text node).
  const dl = clone.querySelector<HTMLElement>("[data-tm-download]");
  if (dl) {
    const sep = dl.previousSibling;
    if (sep && sep.nodeType === Node.TEXT_NODE) sep.textContent = "";
    dl.remove();
  }

  // Freeze any live filter selections into static boxes.
  clone
    .querySelectorAll<HTMLSelectElement>("select[data-tm-select]")
    .forEach((sel) => {
      const live = card.querySelector<HTMLSelectElement>(`#${sel.id}`);
      freezeSelect(sel, live);
    });

  // Clear any in-flight entrance animation so cells aren't captured mid-fade.
  clone.querySelectorAll<HTMLElement>("[data-tm-leaf]").forEach((g) => {
    g.style.animation = "";
    g.style.opacity = "1";
  });

  clone.querySelectorAll("svg").forEach((svg) => {
    (svg as SVGElement).style.fontFamily = FONT_STACK;
  });

  const holder = document.createElement("div");
  holder.style.cssText =
    "position:fixed;left:-99999px;top:0;background:#fff;width:" +
    card.offsetWidth +
    "px;padding:16px 16px 20px;";
  holder.appendChild(clone);
  document.body.appendChild(holder);
  try {
    const canvas = await h2c(holder, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
    });
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "wayflyer-ecommerce-stack.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    holder.remove();
  }
}
